<template>
  <div class="ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button" class="btn btn-back" @click="setCompanyStep('companies-map')" />
          Create Company
        </div>
        <div class="col text-right" v-if="$_can('companies.update')">
          <button type="button" class="btn btn-primary ld-ext-right"
                  @click="save"
                  :disabled="buttonDisabled"
                  :class="loadingButton ? 'running' : ''"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" :saved-settings="savedSettings" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button" class="btn btn-back" @click="$router.push('/companies')" />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover" v-if="showTooltipPrefix">
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  @click="save"
                  v-if="$_can('companies.update')"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-8 email-templates" v-if="$_can('leads.follow-up-email-templates.index')">
        <div class="l-header-2">Email templates</div>
        <p @click="$_can('automated-emails.update') && toggleSendAutomaticEmails()">
          <label class="switch" @click.prevent>
            <input type="checkbox"
                   :checked="company.send_automatic_emails"
            />
            <span class="check-slider round" />
          </label>Send automated emails
        </p>
        <div v-show="company.send_automatic_emails">
          <h6>
            We are sending default emails when certain actions are taken. You can toggle it from default and add your
            own text so they would contain information that matches your company needs.
          </h6>
          <div class="email-templates-wrap rounded-top">
            <p v-for="templateType in templateTypes"
               @click="$_can('leads.follow-up-email-templates.store') && openModalEmailTemplate(templateType.id)"
               :key="templateType.id"
            >
              <label class="switch">
                <input type="checkbox"
                       :checked="editedTemplates.find(e => e.email_type_id === templateType.id)"
                       :disabled="!$_can('leads.follow-up-email-templates.store')"
                />
                <span class="check-slider round" />
              </label>
              {{ templateType.name }}<span class="mod-icon mod-icon-edit" />
            </p>
          </div>
          <div class="mt-3 mb-3">
            <div class="select-wrap">
              <label>Send copy of automated emails to</label>
              <input v-model="company.email_copy"
                     class="form-control"
                     :disabled="!$_can('companies.update')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-if="showModalEmailTemplate" @close="closeModal" :size="'modal-large'">
      <div slot="header">
        <h4>Email template</h4>
      </div>
      <div slot="body">
        <div class="row mb-2">
          <div class="col pr-1">
            <div class="select-wrap">
              <label>Company</label>
              <p class="mx-3">{{ company.name }}</p>
            </div>
            <error-field :errors="errors" field="company_id" />
          </div>
          <div class="col pl-1">
            <div class="select-wrap">
              <label>Types</label>
              <p class="mx-3">{{ templateTypes.find(e => e.id === template.email_type_id).name }}</p>
            </div>
            <error-field :errors="errors" field="email_type_id" />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col">
            <div class="select-wrap">
              <label>Email subject</label>
              <input class="form-control" v-model="template.subject" ref="subject"
                     @click="addingVariableOn = 'subject'"
              />
            </div>
            <error-field :errors="errors" field="subject" />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col">
            <div class="select-wrap h-auto">
              <label>Email text</label>
              <textarea class="form-control email-templates-textarea" contenteditable="true"
                        v-model="template.content" ref="ta" @click="addingVariableOn = 'ta'"
              />
            </div>
            <error-field :errors="errors" field="content" />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col">
            <label>Variables</label>
            <div>
              <span class="badge badge-primary mx-1 unselectable pointer"
                    v-for="(variable, key, index) in template.variables"
                    :key="index"
                    @dblclick="insertVariable(variable)"
              >{{ key }}</span>
            </div>
          </div>
        </div>
        <div v-if="template.available_settings && Object.keys(template.available_settings).length" class="row">
          <div class="col">
            <label>Settings</label>
            <div class="row">
              <div v-for="(setting, index) in template.available_settings" :key="index" class="col-12 col-sm-12 col-md-3 mr-2">
                <div v-if="template.settings" class="select-wrap">
                  <label>{{ setting.label }}</label>
                  <input class="form-control" v-model="template.settings[index]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <div class="row">
          <div class="col-12 text-right">
            <button type="button" class="btn btn-secondary" @click="deleteTemplate"
                    v-if="$_can('leads.follow-up-email-templates.destroy')"
            >
              Delete template
            </button>
            <button type="button" class="btn btn-primary ld-ext-right"
                    @click="saveEmailTemplate"
                    v-if="$_can('leads.follow-up-email-templates.update')"
                    :class="loadingButton ? 'running' : ''"
                    :disabled="buttonDisabled"
            >
              Save template
              <div class="ld ld-ball ld-flip" />
            </button>
          </div>
        </div>
        <div class="clearfix" />
      </div>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapActions, mapMutations, mapState } from 'vuex';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';
import CompaniesStepsMenu from './CompaniesStepsMenu';
import Modal from '../Modal';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';

export default {
  components: {
    CompaniesVisibilityBar,
    Modal,
    CompaniesStepsMenu,
    ErrorField,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
  },
  data: () => ({
    move_sizes: [],
    templateTypes: [],
    showModalEmailTemplate: false,
    addingVariableOn: 'subject',
    email_text: '',
    templates: {},
    template: {
      available_settings: {},
      company_id: null,
      content: '',
      email_type_id: null,
      settings: {},
      subject: '',
      variables: [],
    },
    loadingButton: false,
    buttonDisabled: false,
    showTooltipPrefix: false,
    editedTemplates: [],
    savedSettings: false,
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      company: state => state.companies.company,
      companies: state => state.companies.companies.data,
    }),
  },
  created() {
    this.getEmailTemplateTypes();
    this.getEditedTemplates();
  },
  methods: {
    ...mapActions(['getCompanies']),
    ...mapMutations(['setCompanyStep', 'setCompany', 'setSavedSettings']),
    save() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .patch(`/companies/${this.company.id}`, {
          email_copy: this.company.email_copy,
        })
        .then(() => {
          this.$emit('saved');
          this.getCompanies();
          this.setSavedSettings(true);
          toastr.success('Successfully saved copy of automated emails');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    closeModal() {
      this.showModalEmailTemplate = false;
      this.errors.clear();
    },
    clearError(event) {
      this.errors.clear(event.target.name);
    },
    loadCompany() {
      axios
        .get(`companies/${this.company.id}`)
        .then(res => {
          res.data.data.zip = res.data.data.zip.zip;
          this.setCompany(res.data.data);
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    getEditedTemplates() {
      axios
        .get(`/companies/${this.company.id}/email-templates`)
        .then(res => {
          this.editedTemplates = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    getEmailTemplateTypes() {
      axios
        .get(`values/email-template-types`)
        .then(res => {
          this.templateTypes = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    insertVariable(variable) {
      const textarea = this.addingVariableOn === 'subject' ? this.$refs.subject : this.$refs.ta;
      const cursorPosition = textarea.selectionStart;
      const textBefore = textarea.value.substring(0, cursorPosition);
      const textAfter = textarea.value.substring(cursorPosition, textarea.value.length);
      textarea.value = textBefore + variable + textAfter;
      if (this.addingVariableOn === 'subject') {
        this.template.subject = textarea.value;
      } else {
        this.template.content = textarea.value;
      }
      if (textarea.setSelectionRange) {
        textarea.focus();
        textarea.setSelectionRange(
          cursorPosition + variable.length,
          cursorPosition + variable.length,
        );
      }
    },
    openModalEmailTemplate(type) {
      this.template.email_type_id = type;
      axios
        .get(`/companies/${this.company.id}/email-templates/${this.template.email_type_id}`)
        .then(res => {
          const templateSettings = res.data.data.available_settings;
          this.$set(this.template, 'available_settings', templateSettings);
          this.$set(this.template, 'settings', {});
          Object.keys(templateSettings).forEach(key => {
            this.$set(
              this.template.settings,
              key,
              (res.data.data.settings && res.data.data.settings[key]) ||
                templateSettings[key].default,
            );
          });
          this.$set(this.template, 'subject', res.data.data.subject);
          this.$set(this.template, 'content', res.data.data.content);
          this.$set(this.template, 'variables', res.data.data.variables);
        })
        .catch(err => this.errors.record(err.response.data.error));
      this.showModalEmailTemplate = true;
    },
    deleteTemplate() {
      axios
        .delete(`companies/${this.company.id}/email-templates/${this.template.email_type_id}`)
        .then(() => {
          toastr.success('Successfully deleted email template');
          this.template = {};
          this.closeModal();
          // this.getAllTemplates()
          this.getEditedTemplates();
        })
        .catch(() => {
          toastr.error('Error deleting email template', '', { timeOut: 0 });
        });
    },
    saveEmailTemplate() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .put(`companies/${this.company.id}/email-templates`, { ...this.template })
        .then(() => {
          toastr.success('Successfully saved email template');
          this.template = {};
          this.closeModal();
          this.getEditedTemplates();
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    toggleSendAutomaticEmails() {
      axios
        .patch(`companies/${this.company.id}`, {
          send_automatic_emails: !this.company.send_automatic_emails,
        })
        .then(() => {
          this.loadCompany();
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        });
    },
  },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/companies/CompaniesEmails.vue